import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle response errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

class UserService {
    // User Management
    async getUsers(params = {}) {
        try {
            const response = await api.get('/users', { params });
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async getUser(id) {
        try {
            const response = await api.get(`/users/${id}`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async createUser(userData) {
        try {
            const response = await api.post('/users', userData);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async updateUser(id, userData) {
        try {
            const response = await api.put(`/users/${id}`, userData);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async deleteUser(id) {
        try {
            const response = await api.delete(`/users/${id}`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Role Management
    async getRoles() {
        try {
            const response = await api.get('/roles');
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async createRole(roleData) {
        try {
            const response = await api.post('/roles', roleData);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async updateRole(id, roleData) {
        try {
            const response = await api.put(`/roles/${id}`, roleData);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async deleteRole(id) {
        try {
            const response = await api.delete(`/roles/${id}`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Permission Management
    async getPermissions() {
        try {
            const response = await api.get('/permissions');
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Error handling
    handleError(error) {
        if (error.response) {
            // Server responded with error status
            const message = error.response.data?.message || 'An error occurred';
            return new Error(message);
        } else if (error.request) {
            // Request was made but no response received
            return new Error('Network error - please check your connection');
        } else {
            // Something else happened
            return new Error('An unexpected error occurred');
        }
    }
}

export default new UserService(); 